import { Badge, Banner, Button, Empty, Loader, Surface, Table, Tabs, Text } from '@cloudflare/kumo'
import { ArrowsClockwise, SealCheck } from '@phosphor-icons/react'
import { EXPERT_REVIEW_ITEM_LABELS, type AdminExpertReviewView } from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { HelpPanel } from '../components/help-panel'
import { PageHeader } from '../components/page-header'
import { expertReviewsApi } from '../expert-reviews-api'
import { useGetToken } from '../lib/auth'
import { EXPERT_REVIEW_STATUS_LABEL } from '../lib/expert-review-labels'
import { fmtInt, fmtTime, toMessage } from '../lib/format'

type View = 'open' | 'closed'

/** 열린 요청의 기한 배지. 기한이 지난 '요청됨'은 다음 매시 크론이 반려하고 검수권을 돌려준다 */
function dueBadge(r: AdminExpertReviewView) {
  const hours = Math.floor((Date.parse(r.dueAt) - Date.now()) / 3_600_000)
  if (hours < 0) return <Badge variant="error">기한 지남</Badge>
  return <Badge variant={hours < 24 ? 'error' : 'secondary'}>{fmtInt(hours)}시간 남음</Badge>
}

/**
 * 전문가 검수 대기열 (로드맵 매출 8위). 사용자가 결제하고 보낸 검수 요청을 맡아 항목별 의견을 돌려주거나 반려한다.
 * 지금은 관리자면 누구나 처리한다 — 운영자 권한 분리(C52) 뒤 '검수 담당' 권한으로 나눈다
 */
export function ExpertReviewsPage() {
  const getToken = useGetToken()
  const navigate = useNavigate()
  const [view, setView] = useState<View>('open')
  const [items, setItems] = useState<AdminExpertReviewView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    setBusy(true)
    try {
      setItems((await expertReviewsApi.list(getToken, view)).items)
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }, [getToken, view])

  useEffect(() => {
    setItems(null)
    void reload()
  }, [reload])

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="전문가 검수"
        description="사용자가 결제하고 보낸 검수 요청이에요. 열린 요청은 오래된 것부터 보여요."
        actions={
          <Button
            variant="secondary"
            icon={<ArrowsClockwise />}
            loading={busy}
            onClick={() => void reload()}
          >
            새로고침
          </Button>
        }
      />
      <HelpPanel
        id="expert-reviews"
        steps={[
          <>요청을 열어 "맡기" 를 누르면 진행 중이 되고, 기한이 지나도 자동 반려되지 않아요.</>,
          <>
            [시뮬레이션] 에서 결과를 보고, 요청한 항목마다 판정과 의견을 써서 "결과 보내기" 를
            눌러요.
          </>,
          <>검수하기 어려우면 사유를 써서 반려해요. 쓴 검수권은 사용자에게 돌아가요.</>,
        ]}
        notes={[
          '아무도 맡지 않은 요청은 처리 기한(설정 › 전문가 검수)이 지나면 서버가 반려하고 검수권을 돌려줘요.',
          '결과와 반려 사유는 사용자 앱에 그대로 보이고 거래성 알림이 가요.',
        ]}
      />

      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}

      <Tabs
        variant="underline"
        size="sm"
        value={view}
        onValueChange={(v) => setView(v as View)}
        tabs={[
          { value: 'open', label: '처리할 요청' },
          { value: 'closed', label: '끝난 요청' },
        ]}
      />

      <Surface className="overflow-x-auto rounded-lg p-0">
        {items === null ? (
          <div className="flex items-center gap-3 p-6">
            <Loader />
            <Text variant="secondary">불러오는 중…</Text>
          </div>
        ) : items.length === 0 ? (
          <Empty
            icon={<SealCheck size={40} className="text-kumo-inactive" />}
            title={view === 'open' ? '처리할 검수 요청이 없어요' : '끝난 검수 요청이 없어요'}
            description="새 요청이 들어오면 운영 알림이 가고 여기에 보여요."
          />
        ) : (
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>요청 시각</Table.Head>
                <Table.Head>제품</Table.Head>
                <Table.Head>항목</Table.Head>
                <Table.Head>상태</Table.Head>
                <Table.Head>담당</Table.Head>
                <Table.Head />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((r) => (
                <Table.Row key={r.id}>
                  <Table.Cell className="whitespace-nowrap text-xs">
                    <div>{fmtTime(r.createdAt)}</div>
                    {view === 'open' && dueBadge(r)}
                  </Table.Cell>
                  <Table.Cell className="max-w-48">
                    <div className="truncate font-medium">{r.productName || '이름 없음'}</div>
                  </Table.Cell>
                  <Table.Cell className="max-w-xs text-xs">
                    {r.items.map((i) => EXPERT_REVIEW_ITEM_LABELS[i].ko).join(', ')}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <Badge variant={r.status === 'rejected' ? 'error' : 'secondary'}>
                      {EXPERT_REVIEW_STATUS_LABEL[r.status]}
                    </Badge>
                    {r.closeReason === 'expired' && (
                      <div className="text-kumo-subtle text-xs">기한 초과</div>
                    )}
                    {r.creditReturned && (
                      <div className="text-kumo-subtle text-xs">검수권 반환</div>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-xs">{r.assigneeEmail ?? '—'}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void navigate(`/expert-reviews/${encodeURIComponent(r.id)}`)}
                    >
                      열기
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>
    </div>
  )
}
